import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import TranslationNS from '@/utils/Constants/TranslationNS'
import { isValidEmail } from '@/utils'
import EventHubDialog, {
    EVENT_HUB_DIALOG_BUTTON_CLASS,
    EVENT_HUB_DIALOG_GHOST_CLASS,
    EVENT_HUB_DIALOG_Z,
    EventHubDialogActions,
} from '@/components/EventHub/EventHubDialog'
import EventHubConfirmDialog from '@/components/EventHub/EventHubConfirmDialog'
import { ORG_BTN_DANGER, ORG_BTN_PRIMARY, ORG_EMPTY, ORG_INPUT } from '@/components/Organizer/shared'

/**
 * "Invite a judge / check-in attendant / media" (new design, 15 Oct 2026): one line of context, the
 * add line (email + ONE primary "Invite", Enter submits, inline error), the invited accounts with
 * "Remove" behind a confirmation, "Nobody invited yet" when empty. The list is the shell's event
 * summary: `onChange(listProp, updater)` patches it so the menu and a reopened dialog agree.
 */
export default function InviteAccessDialog({ role, competition, onChange, onClose }) {
    const { t } = useTranslation(TranslationNS.CHECKIN)
    const inputRef = useRef(null)
    const [email, setEmail] = useState('')
    const [error, setError] = useState(null)
    const [done, setDone] = useState(null)
    const [busy, setBusy] = useState(false)
    const [removing, setRemoving] = useState(null)

    const accounts = competition?.[role.listProp] || []
    const noun = t(role.noun)

    const invite = async (event) => {
        event.preventDefault()
        const value = email.trim().toLowerCase()
        setDone(null)
        if (!isValidEmail(value)) {
            setError(t('Please enter a correct e-mail address'))
            return
        }
        if (accounts.some((account) => String(account.email).toLowerCase() === value)) {
            setError(t('{{email}} already has access', { email: value }))
            return
        }
        setBusy(true)
        setError(null)
        try {
            const response = await role.add(competition._id, value)
            const account = { id: String(response.data?.userId), email: value }
            onChange(role.listProp, (previous) => [...(previous || []), account])
            setEmail('')
            setDone(t('Invitation sent to {{email}}', { email: value }))
            inputRef.current?.focus()
        } catch (requestError) {
            console.error('/InviteAccessDialog invite - error', requestError)
            setError(requestError?.response?.data?.message || t('Oops, an error occurred !'))
        }
        setBusy(false)
    }

    const remove = async () => {
        const account = removing
        setRemoving(null)
        setBusy(true)
        setDone(null)
        try {
            await role.remove(competition._id, String(account.id))
            onChange(role.listProp, (previous) =>
                (previous || []).filter((item) => String(item.id) !== String(account.id)),
            )
            setDone(t('{{account}} account access removed successfully!', { account: noun }))
        } catch (requestError) {
            console.error('/InviteAccessDialog remove - error', requestError)
            setError(t('Oops, an error occurred !'))
        }
        setBusy(false)
    }

    return (
        <>
            <EventHubDialog
                id={`invite-${role.key}`}
                title={t(role.title)}
                zClass={EVENT_HUB_DIALOG_Z.UPLOAD}
                width="md"
                initialFocusRef={inputRef}
                onClose={onClose}
            >
                <p className="m-0 text-sm leading-[1.45] text-[#5b5f6e]">{t(role.intro)}</p>
                <form className="flex flex-col gap-2" onSubmit={invite} noValidate>
                    <label className="org-field">
                        <span className="org-field-lbl">{t('Email address')}</span>
                        <div className="flex gap-2">
                            <input
                                ref={inputRef}
                                className={ORG_INPUT}
                                type="email"
                                autoComplete="off"
                                value={email}
                                placeholder={t("e-mail address of the {{account}}'s scoring.fit account", {
                                    account: noun,
                                })}
                                disabled={busy}
                                onChange={(event) => {
                                    setEmail(event.target.value)
                                    setError(null)
                                }}
                            />
                            <button type="submit" className={ORG_BTN_PRIMARY} disabled={busy || !email.trim()}>
                                {busy ? t('Inviting…') : t('Invite')}
                            </button>
                        </div>
                        {error ? (
                            <span className="org-note org-note--err" role="alert">
                                {error}
                            </span>
                        ) : done ? (
                            <span className="org-note" role="status">
                                {done}
                            </span>
                        ) : null}
                    </label>
                </form>
                {accounts.length ? (
                    <ul className="org-mail-list" aria-label={t(role.title)}>
                        {accounts.map((account) => (
                            <li key={String(account.id || account.email)}>
                                <span className="who">
                                    <b>{account.email}</b>
                                </span>
                                <button
                                    type="button"
                                    className={ORG_BTN_DANGER}
                                    disabled={busy}
                                    onClick={() => setRemoving(account)}
                                >
                                    {t('Remove')}
                                </button>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className={ORG_EMPTY}>{t('Nobody invited yet')}</p>
                )}
                <EventHubDialogActions single>
                    <button
                        type="button"
                        className={`${EVENT_HUB_DIALOG_BUTTON_CLASS} ${EVENT_HUB_DIALOG_GHOST_CLASS}`}
                        onClick={onClose}
                    >
                        {t('Close')}
                    </button>
                </EventHubDialogActions>
            </EventHubDialog>
            {removing && (
                <EventHubConfirmDialog
                    title={t('Remove this access?')}
                    text={t('{{email}} will no longer have access.', { email: removing.email })}
                    confirmLabel={t('Remove')}
                    cancelLabel={t('Keep it')}
                    onCancel={() => setRemoving(null)}
                    onConfirm={remove}
                />
            )}
        </>
    )
}
